import React, { useState, useEffect, useCallback } from "react";
import { UserPlus, Check, X, Mail, Clock } from "lucide-react";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    arrayUnion,
    arrayRemove,
    addDoc,
} from "firebase/firestore";
import { db, auth } from "../../../Services/firebase-config";

const TeamJoinRequests = ({ currentTeam, eventId, onTeamUpdate }) => {
    const [joinRequests, setJoinRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [processingRequestId, setProcessingRequestId] = useState(null);

    // Fetch join requests for current team
    const fetchJoinRequests = useCallback(async () => {
        if (!currentTeam || !eventId) return;

        try {
            setIsLoading(true);

            const requestsQuery = query(
                collection(db, "teamInvitations"),
                where("teamId", "==", currentTeam.id),
                where("eventId", "==", eventId),
                where("status", "==", "pending"),
                where("type", "==", "join_request")
            );

            const requestsSnapshot = await getDocs(requestsQuery);
            const requests = requestsSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            setJoinRequests(requests);
        } catch (error) {
            console.error("Error fetching join requests:", error);
            setError("Failed to load join requests");
        } finally {
            setIsLoading(false);
        }
    }, [currentTeam, eventId]);

    useEffect(() => {
        fetchJoinRequests();
    }, [fetchJoinRequests]);

    // Accept join request
    const handleAcceptRequest = async (request) => {
        if (!currentTeam || currentTeam.members.length >= 5) {
            setError("Team is already full (5 members maximum)");
            return;
        }

        setProcessingRequestId(request.id);
        setError("");

        try {
            // Check if user is already in another team
            const userTeamsQuery = query(
                collection(db, "hackathonTeams"),
                where("eventId", "==", eventId),
                where("memberIds", "array-contains", request.inviterId) // Using inviterId as the requester's ID
            );

            const userTeamsSnapshot = await getDocs(userTeamsQuery);

            // If user is in another team, remove them first
            for (const teamDoc of userTeamsSnapshot.docs) {
                const teamData = teamDoc.data();
                if (teamDoc.id !== currentTeam.id) {
                    // Find the user in the members array
                    const userMember = teamData.members?.find(
                        (member) => member.id === request.inviterId
                    );

                    if (userMember) {
                        // If the requester is the leader (createdBy) of that team, delete the whole team
                        if (teamData.createdBy === request.inviterId) {
                            try {
                                await deleteDoc(doc(db, "hackathonTeams", teamDoc.id));
                            } catch (err) {
                                console.error("Error deleting team while moving leader:", err);
                            }
                        } else {
                            const teamRef = doc(db, "hackathonTeams", teamDoc.id);
                            await updateDoc(teamRef, {
                                memberIds: arrayRemove(userMember.id),
                                members: arrayRemove(userMember),
                            });
                        }
                    }
                }
            }

            // Get user data for the new member
            const userQuery = query(
                collection(db, "users"),
                where("email", "==", request.inviterEmail)
            );
            const userSnapshot = await getDocs(userQuery);

            if (userSnapshot.empty) {
                setError("User not found");
                setProcessingRequestId(null);
                return;
            }

            const userData = userSnapshot.docs[0].data();
            const userId = userSnapshot.docs[0].id;

            // Create user data object for team member
            const newMemberData = {
                id: userId,
                name: userData.displayName || userData.name || request.inviterName,
                email: request.inviterEmail,
                phoneNumber: userData.phoneNumber || "",
                photoURL: userData.photoURL || "",
                skills: userData.skills || [],
            };

            // Add user to current team
            const teamRef = doc(db, "hackathonTeams", currentTeam.id);
            await updateDoc(teamRef, {
                memberIds: arrayUnion(userId),
                members: arrayUnion(newMemberData),
            });

            // Update request status
            const requestRef = doc(db, "teamInvitations", request.id);
            await updateDoc(requestRef, {
                status: "accepted",
                acceptedAt: new Date(),
            });

            // Create notification for the user
            const notificationData = {
                userId: userId,
                message: `Your request to join team "${currentTeam.name}" has been accepted!`,
                read: false,
                timestamp: new Date(),
                type: "join_request_accepted",
                relatedData: {
                    teamId: currentTeam.id,
                    teamName: currentTeam.name,
                    eventId: eventId,
                },
            };

            await addDoc(collection(db, "notifications"), notificationData);

            // Refresh data
            onTeamUpdate();
            await fetchJoinRequests();

            setSuccessMessage(`${request.inviterName} has been added to your team!`);
            setTimeout(() => setSuccessMessage(""), 5000);

        } catch (error) {
            console.error("Error accepting join request:", error);
            setError("Failed to accept join request: " + error.message);
        } finally {
            setProcessingRequestId(null);
        }
    };

    // Decline join request
    const handleDeclineRequest = async (request) => {
        setProcessingRequestId(request.id);
        setError("");

        try {
            // Update request status
            const requestRef = doc(db, "teamInvitations", request.id);
            await updateDoc(requestRef, {
                status: "declined",
                declinedAt: new Date(),
            });

            // Create notification for the user
            const userQuery = query(
                collection(db, "users"),
                where("email", "==", request.inviterEmail)
            );
            const userSnapshot = await getDocs(userQuery);

            if (!userSnapshot.empty) {
                const userId = userSnapshot.docs[0].id;

                const notificationData = {
                    userId: userId,
                    message: `Your request to join team "${currentTeam.name}" has been declined.`,
                    read: false,
                    timestamp: new Date(),
                    type: "join_request_declined",
                    relatedData: {
                        teamId: currentTeam.id,
                        teamName: currentTeam.name,
                        eventId: eventId,
                    },
                };

                await addDoc(collection(db, "notifications"), notificationData);
            }

            await fetchJoinRequests();

            setSuccessMessage("Join request declined");
            setTimeout(() => setSuccessMessage(""), 3000);

        } catch (error) {
            console.error("Error declining join request:", error);
            setError("Failed to decline join request: " + error.message);
        } finally {
            setProcessingRequestId(null);
        }
    };

    if (!currentTeam) {
        return null;
    }

    return (
        <div className="space-y-4">
            {/* Success Message */}
            {successMessage && (
                <div className="fixed z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md shadow-lg max-w-md left-1/2 transform -translate-x-1/2 top-4 sm:top-4 sm:left-auto sm:translate-x-0 sm:right-4">
                    <div className="flex items-center gap-2">
                        <Check className="w-5 h-5" />
                        <span className="text-sm font-medium truncate">{successMessage}</span>
                        <button
                            onClick={() => setSuccessMessage("")}
                            className="ml-2 text-green-700 hover:text-green-900 transition-colors"
                            aria-label="dismiss success message"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Header */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <UserPlus className="w-5 h-5 text-custom-blue-1" />
                <h3 className="text-lg font-semibold text-gray-800">
                    Join Requests
                </h3>
                {joinRequests.length > 0 && (
                    <span className="bg-custom-blue-1 text-white text-xs px-2 py-1 rounded-full">
                        {joinRequests.length}
                    </span>
                )}
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-custom-blue-1"></div>
                </div>
            )}

            {/* Join Requests List */}
            {!isLoading && joinRequests.length > 0 ? (
                <div className="space-y-3">
                    {joinRequests.map((request) => (
                        <div
                            key={request.id}
                            className="border border-gray-200 rounded-lg p-4 bg-blue-50"
                        >
                            <div className="flex items-start justify-between flex-col sm:flex-row gap-3">
                                <div className="flex items-start gap-3 w-full sm:w-auto">
                                    <div className="w-10 h-10 min-w-[40px] rounded-full bg-custom-blue-1 flex items-center justify-center">
                                        <Mail className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-gray-800 truncate">
                                            {request.inviterName}
                                        </h4>
                                        <p className="text-sm text-gray-600 truncate">{request.inviterEmail}</p>
                                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                            <Clock className="w-3 h-3" />
                                            <span className="truncate">
                                                {new Date(request.createdAt?.toDate?.() || request.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-2 w-full sm:w-auto flex-col sm:flex-row">
                                    <button
                                        onClick={() => handleAcceptRequest(request)}
                                        disabled={
                                            processingRequestId === request.id ||
                                            currentTeam.members.length >= 5
                                        }
                                        className="flex items-center justify-center gap-1 px-3 py-2 bg-custom-green-1 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors w-full sm:w-auto"
                                    >
                                        {processingRequestId === request.id ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                        ) : (
                                            <Check className="w-4 h-4" />
                                        )}
                                        <span className="truncate">Accept</span>
                                    </button>

                                    <button
                                        onClick={() => handleDeclineRequest(request)}
                                        disabled={processingRequestId === request.id}
                                        className="flex items-center justify-center gap-1 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors w-full sm:w-auto"
                                    >
                                        {processingRequestId === request.id ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                        ) : (
                                            <X className="w-4 h-4" />
                                        )}
                                        <span className="truncate">Decline</span>
                                    </button>
                                </div>
                            </div>

                            {currentTeam.members.length >= 5 && (
                                <div className="mt-2 text-xs text-red-600">
                                    Team is full. Cannot accept more members.
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : !isLoading && (
                <div className="text-center py-8 text-gray-500">
                    <UserPlus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No pending join requests</p>
                </div>
            )}
        </div>
    );
};

export default TeamJoinRequests;